import { Assessment as AssessmentIcon, Close as CloseIcon, OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { Box, Chip, IconButton } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Context } from '../../service/Context';
import theme from '../../styles/theme';
import { capitalizar } from '../../utils/UtilesComun';
import { ButtonApp, DialogApp, TypographyApp } from '../custom/CustomComponents';
import TablaHistorialRendimiento from './TablaHistorialRendimiento';

/**
 * Modal que muestra el historial completo de partidos, estadísticas individuales y
 * valoraciones cualitativas y cuantitativas de un jugador en una temporada.
 */
const ModalHistorialRendimiento = ({ open, onClose, jugador, temporada }) => {
  const navigate = useNavigate();
  const { obtenerHistorialEvaluacionesJugador, jugadoresCache, setJugadorCacheItem } = useContext(Context);

  const [partidos, setPartidos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const idJugador = jugador?.idJugador;

  useEffect(() => {
    let isMounted = true;

    const cargarHistorial = async () => {
      if (!open || !idJugador) {
        return;
      }

      const tempKey = temporada || 'DEFAULT';
      const cached = jugadoresCache?.[idJugador]?.temporadas?.[tempKey]?.partidosRendimiento;

      if (cached && Array.isArray(cached)) {
        setPartidos(cached);
        setLoading(false);
      } else {
        setPartidos([]);
        setLoading(true);
      }
      setError(null);

      try {
        const datos = await obtenerHistorialEvaluacionesJugador(idJugador, temporada);
        if (isMounted) {
          const partidosList = datos || [];
          setPartidos(partidosList);
          if (setJugadorCacheItem) {
            setJugadorCacheItem(idJugador, {
              temporadas: {
                [tempKey]: {
                  partidosRendimiento: partidosList,
                },
              },
            });
          }
        }
      } catch (err) {
        if (isMounted) {
          console.error('Error al cargar historial de rendimiento:', err);
          if (!cached) {
            setError('No se pudo cargar el historial de partidos del jugador.');
          }
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    cargarHistorial();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, idJugador, temporada]);

  if (!jugador) {
    return null;
  }

  return (
    <DialogApp
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          maxWidth: { xs: '96vw', sm: 1080, md: 1240, lg: 1320 },
          width: '100%',
        },
      }}
      title={
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssessmentIcon sx={{ color: theme.palette.primary.main, fontSize: '1.3rem' }} />
            <TypographyApp sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
              Historial de Rendimiento: {capitalizar(jugador.nombreJugador)}
            </TypographyApp>
            {temporada && (
              <Chip
                label={temporada}
                size="small"
                sx={{
                  fontWeight: 600,
                  fontSize: '0.72rem',
                  height: 22,
                  backgroundColor: '#f1f5f9',
                  color: '#475569',
                }}
              />
            )}
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      }
      actions={
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', p: 0.5 }}>
          <ButtonApp
            onClick={() => {
              onClose();
              navigate(`/jugador/${jugador.idJugador}`);
            }}
            startIcon={<OpenInNewIcon fontSize="small" />}
            sx={{ fontSize: '0.8rem' }}
          >
            Ver perfil completo
          </ButtonApp>

          <ButtonApp onClick={onClose} contrast>
            Cerrar
          </ButtonApp>
        </Box>
      }
    >
      <TablaHistorialRendimiento
        partidos={partidos}
        temporada={temporada}
        categoriaFallback={jugador?.categoria}
        notaMediaFallback={jugador?.notaMediaTemporada ?? null}
        loading={loading}
        error={error}
        embedded={false}
        maxHeight="60vh"
        onPartidoClick={(idPartido) => {
          onClose();
          navigate(`/partido/${idPartido}`);
        }}
      />
    </DialogApp>
  );
};

ModalHistorialRendimiento.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  jugador: PropTypes.object,
  temporada: PropTypes.string,
};

export default ModalHistorialRendimiento;
